/**
 * INFINITY LIFE 2.0 — Owner & Administration Control Center
 */

import { store } from '../state.js';
import { api, showToast } from '../api.js';

let adminData = null;
let adminSettings = null;
let adminLogs = [];

export function renderAdminPanel() {
  const { user } = store.get();

  if (!user || (!user.is_owner && user.role !== 'owner')) {
    return `
      <div style="text-align: center; padding: 60px 20px;">
        <div style="font-size: 2.5rem; margin-bottom: 12px;">🔒</div>
        <h2>Acesso Exclusivo do Proprietário</h2>
        <p style="color: var(--text-muted); margin-top: 8px;">
          Esta área é restrita à conta proprietária inicial do Infinity Life.
        </p>
      </div>
    `;
  }

  if (!adminData) {
    loadAdminData();
    return `
      <div style="text-align: center; padding: 60px 20px;">
        <div style="font-size: 2rem; margin-bottom: 12px;">🛡️</div>
        <h2>Carregando Painel Técnico...</h2>
      </div>
    `;
  }

  const { stats, environment } = adminData;
  const s = adminSettings || {};

  return `
    <div class="admin-panel-view">
      <!-- Header -->
      <div class="dashboard-header">
        <div>
          <h1 class="greeting-text">🛡️ Painel do Proprietário</h1>
          <p class="date-text">Centro de controle técnico, infraestrutura Hostinger e segurança.</p>
        </div>
        <div>
          <button class="btn-secondary" id="btn-refresh-admin">
            🔄 Atualizar Diagnósticos
          </button>
        </div>
      </div>

      <!-- Environment & Stats Grid -->
      <div class="metrics-grid">
        <div class="metric-card">
          <div class="metric-label">Usuários Cadastrados</div>
          <div class="metric-value">${stats.users_total}</div>
          <div class="metric-sub">${stats.active_sessions} sessões ativas</div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Tarefas Registradas</div>
          <div class="metric-value">${stats.tasks_total}</div>
          <div class="metric-sub">${stats.pending_reminders} lembretes na fila</div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Versão PHP & MySQL</div>
          <div class="metric-value" style="font-size: 1.3rem;">PHP ${environment.php_version}</div>
          <div class="metric-sub">MySQL / MariaDB: ${environment.mysql_version}</div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Status Hostinger</div>
          <div class="metric-value" style="font-size: 1.3rem; color: var(--emerald-accent);">
            ✓ Operacional
          </div>
          <div class="metric-sub">Extensões PDO, cURL e OpenSSL ativas</div>
        </div>
      </div>

      <!-- Settings Form -->
      <div class="card-section" style="margin-bottom: 28px;">
        <div class="section-title">
          <span>⚙️ Configurações Globais do Sistema</span>
        </div>

        <form id="admin-settings-form">
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Nome da Plataforma</label>
              <input type="text" id="set-app-name" class="form-input" value="${escapeHtml(s.app_name?.value || 'Infinity Life')}" />
            </div>

            <div class="form-group">
              <label class="form-label">Permitir Novos Cadastros Google</label>
              <select id="set-allow-reg" class="form-select">
                <option value="1" ${s.allow_registration?.value !== '0' ? 'selected' : ''}>Sim (Aberto)</option>
                <option value="0" ${s.allow_registration?.value === '0' ? 'selected' : ''}>Não (Privado / Apenas Proprietário)</option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Google OAuth 2.0 Client ID</label>
            <input type="text" id="set-google-client-id" class="form-input" 
                   placeholder="Ex: 123456789-abcdef.apps.googleusercontent.com" 
                   value="${escapeHtml(s.google_client_id?.value || '')}" />
            <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 4px;">
              Configurado no Google Cloud Console para o domínio de hospedagem na Hostinger.
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Google Gemini API Key (IA)</label>
              <input type="password" id="set-gemini-key" class="form-input" 
                     placeholder="Insira a chave de API do Gemini..." 
                     value="${escapeHtml(s.gemini_api_key?.value || '')}" />
              <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 4px;">
                ${s.gemini_api_key?.has_value ? '✓ Chave gravada de forma protegida no servidor.' : 'Nenhuma chave cadastrada.'}
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">Modelo Padrão do Gemini</label>
              <select id="set-gemini-model" class="form-select">
                <option value="gemini-1.5-flash" ${s.gemini_model?.value === 'gemini-1.5-flash' ? 'selected' : ''}>Gemini 1.5 Flash (Rápido e Econômico)</option>
                <option value="gemini-1.5-pro" ${s.gemini_model?.value === 'gemini-1.5-pro' ? 'selected' : ''}>Gemini 1.5 Pro (Avançado)</option>
              </select>
            </div>
          </div>

          <div class="form-group" style="max-width: 300px;">
            <label class="form-label">Limite Diário de Chamadas IA (Por Usuário)</label>
            <input type="number" id="set-gemini-limit" class="form-input" value="${s.gemini_daily_limit?.value || '50'}" min="5" max="500" />
          </div>

          <!-- Asaas Gateway Configuration -->
          <div style="margin-top: 20px; padding-top: 16px; border-top: 1px dashed var(--border-subtle);">
            <h4 style="font-size: 0.95rem; font-weight: 700; margin-bottom: 12px; display: flex; align-items: center; gap: 8px;">
              <span>💳</span> <span>Integração de Pagamento Asaas (Checkout)</span>
            </h4>

            <div class="form-group">
              <label class="form-label">Link de Pagamento Asaas (Checkout URL)</label>
              <input type="url" id="set-asaas-url" class="form-input" 
                     placeholder="https://www.asaas.com/c/seu_link_aqui" 
                     value="${escapeHtml(s.asaas_payment_url?.value || '')}" />
              <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 4px;">
                Link do link de cobrança gerado no painel do Asaas. O cliente será direcionado para ele ao clicar nos botões da página de vendas.
              </div>
            </div>
          </div>

          <div style="display: flex; justify-content: flex-end; margin-top: 20px;">
            <button type="submit" class="btn-primary" id="btn-save-settings">
              <span>💾 Salvar Configurações</span>
            </button>
          </div>
        </form>
      </div>

      <!-- Security Audit Logs -->
      <div class="card-section">
        <div class="section-title">
          <span>📜 Log de Auditoria & Segurança</span>
        </div>

        <div style="overflow-x: auto;">
          <table style="width: 100%; border-collapse: collapse; font-size: 0.85rem; text-align: left;">
            <thead>
              <tr style="border-bottom: 1px solid var(--border-subtle); color: var(--text-muted);">
                <th style="padding: 10px;">Data/Hora</th>
                <th style="padding: 10px;">Ação</th>
                <th style="padding: 10px;">Entidade</th>
                <th style="padding: 10px;">Usuário</th>
                <th style="padding: 10px;">IP</th>
              </tr>
            </thead>
            <tbody>
              ${adminLogs.length === 0 ? `
                <tr><td colspan="5" style="padding: 20px; text-align: center; color: var(--text-muted);">Nenhum log registrado ainda.</td></tr>
              ` : `
                ${adminLogs.map((log) => `
                  <tr style="border-bottom: 1px solid var(--border-subtle);">
                    <td style="padding: 10px; color: var(--text-secondary);">${log.created_at}</td>
                    <td style="padding: 10px; font-weight: 600; color: var(--text-primary);">${escapeHtml(log.action)}</td>
                    <td style="padding: 10px; color: var(--text-muted);">${escapeHtml(log.entity_type)}</td>
                    <td style="padding: 10px;">${escapeHtml(log.user_email || 'Sistema')}</td>
                    <td style="padding: 10px; color: var(--text-muted);">${escapeHtml(log.ip_address || '-')}</td>
                  </tr>
                `).join('')}
              `}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;
}

async function loadAdminData() {
  try {
    const [ovRes, setRes, logRes] = await Promise.all([
      api.getAdminOverview(),
      api.getAdminSettings(),
      api.getAdminLogs(),
    ]);

    if (ovRes.success) adminData = ovRes.data;
    if (setRes.success) adminSettings = setRes.settings;
    if (logRes.success) adminLogs = logRes.data || [];

    window.dispatchEvent(new CustomEvent('infinity:rerender-view'));
  } catch (err) {
    console.error('Falha ao carregar dados administrativos:', err);
  }
}

function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

export function attachAdminPanelEvents(container) {
  const refreshBtn = container.querySelector('#btn-refresh-admin');
  if (refreshBtn) {
    refreshBtn.addEventListener('click', () => {
      adminData = null;
      loadAdminData();
      showToast('Dados atualizados com sucesso.', 'info');
    });
  }

  const form = container.querySelector('#admin-settings-form');
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const app_name = container.querySelector('#set-app-name').value.trim();
      const allow_registration = container.querySelector('#set-allow-reg').value;
      const google_client_id = container.querySelector('#set-google-client-id').value.trim();
      const gemini_api_key = container.querySelector('#set-gemini-key').value.trim();
      const gemini_model = container.querySelector('#set-gemini-model').value;
      const gemini_daily_limit = container.querySelector('#set-gemini-limit').value.trim();
      const asaas_payment_url = container.querySelector('#set-asaas-url')?.value.trim() || '';

      try {
        await api.updateAdminSettings({
          app_name,
          allow_registration,
          google_client_id,
          gemini_api_key,
          gemini_model,
          gemini_daily_limit,
          asaas_payment_url,
        });

        showToast('Configurações do sistema salvas com sucesso!', 'success');
        adminData = null;
        loadAdminData();
      } catch (err) {
        showToast(err.message, 'error');
      }
    });
  }
}
